"use client";

/**
 * RudderStackPageEvents — page-level sheet-aligned events: page_open + tab_switched.
 *
 * DECOUPLED: to remove, delete this file + its single <RudderStackPageEvents />
 * mount in PageScript.tsx. Reuses the shared SDK (same write key / data plane).
 *
 * TIMING NOTE: the RudderStack SDK is loaded lazily (DeferUntilInteraction →
 * first interaction or ~4.5s backstop), so it doesn't exist at first paint.
 * We poll briefly for readiness and fire page_open the moment the SDK is up,
 * so it's never silently dropped. Net: page_open fires at SDK-ready (first
 * interaction / backstop), NOT at initial paint — a consequence of the
 * deferred-loading perf optimization, not this code.
 */

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import { trackPageOpen, trackTabSwitched } from "@/customAnalytics/rudderStackEvents";

const isSdkReady = (): boolean =>
  typeof window !== "undefined" &&
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof (window as any).rudderanalytics?.track === "function";

/**
 * How long to wait for the SDK before giving up. A university with no RudderStack write
 * key configured (university_app_config.rudder_stack_source_write_key) never loads the SDK
 * at all, so an unbounded poll would tick for the whole page visit and never succeed.
 * DeferUntilInteraction mounts the tag block on first interaction or a ~4.5s backstop, so
 * the SDK is always up well inside this window if it is going to load.
 */
const SDK_WAIT_MS = 30_000;

export default function RudderStackPageEvents() {
  const pathname = usePathname();
  const lastFiredPath = useRef<string | null>(null);

  // page_open — once per page view (initial load + each SPA route change).
  // Fired as soon as the SDK is ready so the lazy load can't drop it.
  useEffect(() => {
    if (lastFiredPath.current === pathname) return; // dedup (incl. StrictMode)
    let fired = false;
    const fire = (): boolean => {
      if (fired || !isSdkReady()) return false;
      fired = true;
      lastFiredPath.current = pathname;
      trackPageOpen();
      return true;
    };
    if (fire()) return;
    // Bounded: stop once the SDK is up OR the wait window elapses, so a university with no
    // write key (SDK never loads) doesn't poll for the entire page visit.
    const deadline = Date.now() + SDK_WAIT_MS;
    const id = window.setInterval(() => {
      if (fire() || Date.now() >= deadline) window.clearInterval(id);
    }, 300);
    return () => window.clearInterval(id);
  }, [pathname]);

  // tab_switched — when the tab is hidden (switched away / minimized).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") trackTabSwitched();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  return null;
}
